import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SESSION_KEY = "assistant.session_id";

/**
 * SoilHaul Assistant — floating chat widget mounted app-wide.
 *  - Auto-creates a per-user session, persisted in localStorage.
 *  - Sends {message, session_id} → /api/assistant/chat.
 *  - Server enforces role permissions and executes actions (e.g. status changes).
 */
export default function AssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY));
  const [messages, setMessages] = useState([]); // {role, content, at}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const { data } = await api.post("/assistant/sessions");
      localStorage.setItem(SESSION_KEY, data.session_id);
      setSessionId(data.session_id);
      return data.session_id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assistant unavailable");
      return null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && !sessionId) ensureSession();
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, sessionId, ensureSession]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const sid = await ensureSession();
    if (!sid) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sid, message: text });
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply,
        at: new Date().toISOString(),
        action_result: data.action_result,
      }]);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${msg}`, at: new Date().toISOString(), error: true }]);
    } finally { setBusy(false); }
  };

  const resetSession = async () => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
  };

  if (!user) return null;

  return (
    <>
      {/* Toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-toggle"
          aria-label="Open SoilHaul Assistant"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gray-900 hover:bg-orange-500 text-white shadow-lg flex items-center justify-center transition-colors"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={2} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-white border border-gray-200 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-900 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
              <div>
                <div className="font-display font-bold text-sm tracking-tight leading-none">SoilHaul Assistant</div>
                <div className="text-[9px] uppercase tracking-widest text-gray-400 mt-0.5">beta</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetSession}
                title="New conversation"
                data-testid="assistant-new-chat"
                className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-white px-2 py-1"
              >
                Reset
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="assistant-close"
                aria-label="Close"
                className="text-gray-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-xs text-gray-500 font-mono leading-relaxed space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-400">Try asking:</div>
                <ul className="space-y-1 ml-1">
                  <li>· "How many loads are in transit?"</li>
                  <li>· "Any exceptions to review?"</li>
                  <li>· "Where's BOL-202607-0009?"</li>
                  {user?.role !== "driver" && user?.role !== "hauler" && user?.role !== "receiver" && (
                    <li>· "Mark BOL-XXXX as picked_up"</li>
                  )}
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} m={m} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="font-mono">Thinking…</span>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-3 border-t border-gray-200 bg-white flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about loads, exceptions, or actions…"
              data-testid="assistant-input"
              disabled={busy}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-gray-900 font-mono disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || busy}
              data-testid="assistant-send"
              className="w-9 h-9 flex items-center justify-center bg-gray-900 hover:bg-orange-500 text-white disabled:opacity-40 disabled:hover:bg-gray-900 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={isUser ? "assistant-msg-user" : "assistant-msg-assistant"}
        className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-gray-900 text-white"
            : m.error
              ? "bg-red-50 text-red-800 border border-red-200"
              : "bg-white text-gray-900 border border-gray-200"
        }`}
      >
        {m.content}
        {m.action_result?.ok && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-[10px] uppercase tracking-widest text-emerald-700 font-mono">
            ✓ Action executed
          </div>
        )}
        {m.action_result && !m.action_result.ok && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-[10px] uppercase tracking-widest text-red-700 font-mono">
            Action failed: {m.action_result.error}
          </div>
        )}
      </div>
    </div>
  );
}
